import React from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { Link } from '@/src/i18n/navigation'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface CollectionIndexEntryView {
    slug: string
    href: string
    title: string
    description: string
}

interface CollectionIndexProps {
    heading: string
    subheading: string
    entries: CollectionIndexEntryView[]
    emptyLabel: string
    /** Receives the entry's 0-based place in `entries`. */
    onSelect: (index: number) => void
    isMobile: boolean
}

const CollectionIndex: React.FC<CollectionIndexProps> = ({
    heading,
    subheading,
    entries,
    emptyLabel,
    onSelect,
    isMobile
}) => {
    return (
        <Stack spacing={isMobile ? 2.5 : 4}>
            <Box>
                <Typography
                    component="h1"
                    sx={{
                        fontSize: isMobile ? '1.5rem' : '2.25rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: 'textColors.primary'
                    }}
                >
                    {heading}
                </Typography>
                <Typography
                    sx={{
                        fontSize: isMobile ? '0.938rem' : '1.125rem',
                        color: 'textColors.secondary',
                        mt: 1
                    }}
                >
                    {subheading}
                </Typography>
            </Box>

            {entries.length === 0 ? (
                <Typography sx={{ fontSize: '0.938rem', color: 'textColors.subheading' }}>{emptyLabel}</Typography>
            ) : (
                <Grid container spacing={isMobile ? 2 : 3}>
                    {entries.map((entry, index) => (
                        <Grid key={entry.slug} size={{ xs: 12, md: 6 }}>
                            <Box
                                component={Link}
                                href={entry.href}
                                onClick={() => onSelect(index)}
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    height: '100%',
                                    p: isMobile ? 2 : 2.5,
                                    borderRadius: HOLIDAYS_RADIUS.SURFACE,
                                    textDecoration: 'none',
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'borders.default',
                                    transition: 'box-shadow 0.2s, border-color 0.2s',
                                    '&:hover': { boxShadow: 3, borderColor: 'primary.main' }
                                }}
                            >
                                <Stack spacing={0.75} sx={{ flex: 1 }}>
                                    <Typography
                                        component="h2"
                                        sx={{
                                            fontSize: isMobile ? '1rem' : '1.125rem',
                                            fontWeight: 700,
                                            color: 'textColors.primary'
                                        }}
                                    >
                                        {entry.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.875rem', color: 'textColors.secondary' }}>
                                        {entry.description}
                                    </Typography>
                                </Stack>

                                <ArrowForwardIcon
                                    sx={{
                                        fontSize: 20,
                                        color: 'primary.main',
                                        flexShrink: 0,
                                        '[dir=rtl] &': { transform: 'scaleX(-1)' }
                                    }}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Stack>
    )
}

export { CollectionIndex }
export type { CollectionIndexProps, CollectionIndexEntryView }
export default CollectionIndex
